import { Dialog, DialogActions } from '@mui/material';
import { getCompanyLogo } from 'lib/api/company';
import { getConfirmingRequestById } from 'lib/api/confirming';
import { fetchDetails as getCreditRequestDetails } from 'lib/api/creditRequest';
import { expressGetCompanyLogo } from 'lib/api/express';
import { getCompanyPayoutGroup } from 'lib/api/payments';
import CustomButton from 'lib/designSystem/Button';
import { cellFormat } from 'lib/designSystem/Table/cellFormat';
import EnhancedTab from 'lib/designSystem/TabSimulation';
import { showAlert } from 'lib/functions/utils';
import { ConfirmingExportRequest } from 'pages/Finance/Components/Financing/common/SimulationExportDialog/types/ConfirmingExportRequest';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { generateSimulationPDF } from '../../../../../../../../pages/Finance/Components/Financing/common/exportSimulation';
import { generateConfirmingSimulationPDF } from '../../../../../../../../pages/Finance/Components/Financing/common/exportSimulationConfirming';
import { generateSimulationCreditPDF } from '../../../../../../../../pages/Finance/Components/Financing/common/exportSimulationCredit';
import { AuthState } from '../../../../../../../../services/redux/authSlice';
import { PayOutRequest } from '../../types';
import PayOutDetailAppliedDocuments from './components/AppliedDocuments';
import PayOutDetailDocuments from './components/Documents';
import PayOutDetailPayouts from './components/Payouts';
import PayOutDetaiPendingAccounts from './components/PendingAccounts';
import PayOutSummary from './components/Summary';
import PayOutSummaryConfirming from './components/SummaryConfirming';

type TabConfig = {
  index: number;
  label: string;
  component: React.ReactElement;
};

type SourceType =
  | 'factoring-request'
  | 'confirming-request'
  | 'payment-request'
  | 'payment-request-passive'
  | 'credit-request';

interface TabsBySourceProps {
  request: Partial<PayOutRequest> & {
    source: string;
    sourceId: string | number | null;
  };
  setSuccess: (message: string) => void;
  setFailure: (message: string) => void;
  reset: () => void | Promise<void>;
  onExport: () => void | Promise<void>;
  setActiveTab: (tab: number) => void;
  setUp: () => Promise<void>;
  refreshPaymentGroupDetails: (
    source: string,
    sourceId: string,
  ) => Promise<void>;
}

const tabsBySource = ({
  request,
  setSuccess,
  setFailure,
  reset,
  onExport,
  setActiveTab,
  setUp,
  refreshPaymentGroupDetails,
}: TabsBySourceProps): TabConfig[] => {
  const { source } = request;

  const baseSourceProps = {
    source: request.source,
    sourceId: request.sourceId,
    setSuccess,
    setFailure,
    reset,
  };

  const tabs = {
    summary: {
      index: 0,
      label: 'Resumen',
      component: (
        <PayOutSummary
          request={{
            ...request,
            pendingAccounts: request.pendingAccounts ?? [],
            documentsApplied: request.documentsApplied ?? [],
            documents: request.documents ?? [],
            payments: request.payments ?? [],
            businessName: request.businessName ?? '',
            businessIdentifier: request.businessIdentifier ?? '',
            amountToDepositTotal: request.amountToDepositTotal ?? 0,
            guaranteeAmount: request.guaranteeAmount ?? 0,
            amountToApply: request.amountToApply ?? 0,
            amount: request.amount ?? 0,
            createdAt: request.createdAt ?? new Date(),
          }}
          onExport={onExport}
        />
      ),
    },

    confirmingSummary: {
      index: 0,
      label: 'Resumen',
      component: (
        <PayOutSummaryConfirming request={request} onExport={onExport} />
      ),
    },

    payments: {
      index: 0,
      label: 'Pagos',
      component: (
        <PayOutDetailPayouts
          payments={request.payments ?? []}
          setActiveTab={setActiveTab}
          setUp={setUp}
          refreshPaymentGroupDetails={refreshPaymentGroupDetails}
          {...baseSourceProps}
        />
      ),
    },

    appliedDocuments: {
      index: 1,
      label: 'Documentos Aplicados',
      component: (
        <PayOutDetailAppliedDocuments
          documentsApplied={request.documentsApplied ?? []}
          {...baseSourceProps}
        />
      ),
    },

    documents: {
      index: 2,
      label: 'Documentos',
      component: (
        <PayOutDetailDocuments
          documents={request.documents ?? []}
          {...baseSourceProps}
        />
      ),
    },

    pendingAccounts: {
      index: 3,
      label: 'Cuentas pendientes',
      component: (
        <PayOutDetaiPendingAccounts
          pendingAccounts={request.pendingAccounts ?? []}
          {...baseSourceProps}
        />
      ),
    },
  };

  const tabConfigurations: Record<SourceType, TabConfig[]> = {
    'factoring-request': [tabs.summary, tabs.payments],
    'confirming-request': [tabs.confirmingSummary, tabs.payments],
    'payment-request': [tabs.summary, tabs.documents, tabs.payments],
    'payment-request-passive': [
      tabs.summary,
      tabs.pendingAccounts,
      tabs.appliedDocuments,
      tabs.payments,
    ],
    'credit-request': [tabs.summary, tabs.payments],
  };

  return tabConfigurations[source as SourceType] ?? [];
};

interface PayOutDetailsProps {
  paymentGroupedDetails: Partial<PayOutRequest> & {
    source: string;
    sourceId: string | number | null;
    payoutStatusEsp?: string;
    sourceTranslation?: string;
    id?: string | number;
    stakeholderIdentifier?: string;
    stakeholderName?: string;
    subProductName?: string;
    operationDate?: Date | string;
    financingAt?: Date | string;
    advance?: number;
    rate?: number;
    arrearRate?: number;
    expenses?: Array<{ name: string; amount: number; hasIva: boolean }>;
    operationCostFixed?: number;
    operationCostVariable?: number;
    operationCostIva?: number;
    guaranteeObservation?: string;
    [key: string]: unknown;
  };
  open: boolean;
  setOpen: (open: boolean) => void;
  setSuccess: (message: string) => void;
  setFailure: (message: string) => void;
  setUp: () => Promise<void>;
  refreshPaymentGroupDetails: (
    source: string,
    sourceId: string,
  ) => Promise<void>;
}

const PayOutDetails = (props: PayOutDetailsProps) => {
  const {
    paymentGroupedDetails,
    open,
    setOpen,
    setSuccess,
    setFailure,
    setUp,
    refreshPaymentGroupDetails,
  } = props;
  const startValue = 0;
  const [activeTab, setActiveTab] = useState(startValue);
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  const reset = async () => {
    await setUp();
    setOpen(false);
  };

  const handleExport = async () => {
    try {
      const payouts = await getCompanyPayoutGroup(
        dispatch,
        paymentGroupedDetails.source || 'factoring-request',
        paymentGroupedDetails.sourceId as number,
      );

      // Get additional request details for confirming and credit requests (similar to factoring)
      let additionalRequestData: any = {};
      if (paymentGroupedDetails.source === 'confirming-request') {
        try {
          additionalRequestData = await getConfirmingRequestById(
            dispatch,
            paymentGroupedDetails.sourceId as number,
          );
        } catch (error) {
          console.error('Error getting confirming request details:', error);
        }
      } else if (paymentGroupedDetails.source === 'credit-request') {
        try {
          additionalRequestData = await getCreditRequestDetails(
            dispatch,
            paymentGroupedDetails.sourceId as number,
          );
        } catch (error) {
          console.error('Error getting credit request details:', error);
        }
      }

      // Get company logo
      const imageBase64 = user?.express
        ? await expressGetCompanyLogo(dispatch, user.clientId as string)
        : await getCompanyLogo(dispatch);

      // Merge additional request data with paymentGroupedDetails
      const mergedData = { ...paymentGroupedDetails, ...additionalRequestData };

      // Build request object for PDF generation
      const request = {
        id: mergedData.sourceId || mergedData.id,
        executiveName: user?.name || '',
        businessIdentifier:
          mergedData.businessIdentifier ||
          mergedData.stakeholderIdentifier ||
          '',
        businessName:
          mergedData.businessName || mergedData.stakeholderName || 'N/A',
        subProductName:
          mergedData.sourceTranslation || mergedData.subProductName || 'N/A',
        createdAt: new Date(),
        operationDate:
          mergedData.createdAt || mergedData.operationDate || new Date(),
        financingAt:
          mergedData.createdAt || mergedData.financingAt || new Date(),
        amount: mergedData.amount || mergedData.amountToDepositTotal || 0,
        amountToDepositTotal: mergedData.amountToDepositTotal || 0,
        // Handle documents differently based on request type - now with merged data
        documents: mergedData.documents || mergedData.documentsApplied || [],
        // Exclude documentsToApply to prevent aplicaciones section from appearing
        documentsToApply: [],
        payments: mergedData.payments || [],
        source: mergedData.source || '',
        sourceTranslation:
          mergedData.sourceTranslation || mergedData.subProductName || 'N/A',
        // Add other required fields with defaults
        advance: mergedData.advance,
        rate: mergedData.rate,
        arrearRate: mergedData.arrearRate || 0,
        amountToApply: mergedData.amountToApply,
        expenses: mergedData.expenses?.reduce(
          (
            acc: number,
            expense: { name: string; amount: number; hasIva: boolean },
          ) => acc + expense.amount,
          0,
        ),
        operationCostFixed: mergedData.operationCostFixed,
        operationCostVariable: mergedData.operationCostVariable,
        operationCostIva: mergedData.operationCostIva || 0,
        expensesCostIva: mergedData.expenses?.reduce(
          (
            acc: number,
            expense: { name: string; amount: number; hasIva: boolean },
          ) => acc + (expense.hasIva ? expense.amount * 0.19 : 0),
          0,
        ),
        expensesDetails: mergedData.expenses,
        guaranteeAmount: mergedData.guaranteeAmount,
        guaranteeObservation: mergedData.guaranteeObservation || '',
        showAdvance: true,
        showRate: true,
        showArrearRate: true,
        showAmount: true,
        showAmountAdvance: true,
        showAmountRetained: true,
        showAmountDifference: true,
        showAmountComissionIva: true,
        showAmountTotalApplied: true,
        showDraftBalance: true,
        showExpenses: true,
        showGuaranteeAmount: true,
        showExpensesDetails: true,
      };

      if (paymentGroupedDetails.source === 'confirming-request') {
        // Build confirming-specific request object
        const confirmingRequest = {
          ...mergedData,
          showClientInfo: true,
          showRequestInfo: true,
          showDates: true,
          showAdvance: true,
          showRate: true,
          showArrearRate: true,
          showAmounts: true,
          showDocuments: true,
          showOperationCosts: true,
          showExpenses: true,
          showAccountReceivable: true,
        };
        await generateConfirmingSimulationPDF(
          confirmingRequest as unknown as ConfirmingExportRequest,
          user?.name || '',
          imageBase64,
          payouts,
        );
      } else if (paymentGroupedDetails.source === 'credit-request') {
        // Build credit-specific request object
        const creditRequest = {
          id: mergedData.sourceId || mergedData.id,
          businessIdentifier:
            mergedData.businessIdentifier ||
            mergedData.stakeholderIdentifier ||
            '',
          businessName:
            mergedData.businessName || mergedData.stakeholderName || 'N/A',
          creditType: 'Crédito de Consumo', // Default type
          interestType: 'Fijo', // Default type
          startDate: mergedData.createdAt
            ? new Date(mergedData.createdAt).toLocaleDateString('es-CL')
            : new Date().toLocaleDateString('es-CL'),
          firstPaymentDate: mergedData.createdAt
            ? new Date(mergedData.createdAt).toLocaleDateString('es-CL')
            : new Date().toLocaleDateString('es-CL'),
          lastPaymentDate: mergedData.createdAt
            ? new Date(mergedData.createdAt).toLocaleDateString('es-CL')
            : new Date().toLocaleDateString('es-CL'),
          months: 12, // Default
          totalPayments: 12, // Default
          paymentDay: 1, // Default
          totalAmount:
            mergedData.amount || mergedData.amountToDepositTotal || 0,
          operationAmount: 0,
          insuranceAmount: 0,
          totalLoanAmount:
            mergedData.amount || mergedData.amountToDepositTotal || 0,
          totalCost: mergedData.amount || mergedData.amountToDepositTotal || 0,
          rate: 0,
          anualRate: 0,
          arrearRate: 0,
          anualArrearRate: 0,
          cae: 0,
          warranties: [],
          amortizationSchedule: [],
          documents: mergedData.documents || mergedData.documentsApplied || [],
          showAmount: true,
          showOperationExpenses: true,
          showInsurance: true,
          showTotalCredit: true,
          showCreditCost: true,
          showMonthlyInterest: true,
          showAnnualInterest: true,
          showArrearInterest: true,
          showAnnualArrearInterest: true,
          showCAE: true,
          showWarranties: true,
          showPaymentTable: true,
        };
        await generateSimulationCreditPDF(
          creditRequest,
          user?.name || '',
          imageBase64,
          payouts,
        );
      } else {
        // Default to factoring for factoring-request and other types+
        await generateSimulationPDF(
          request,
          user?.name || '',
          imageBase64,
          payouts,
        );
      }

      showAlert(dispatch, 'success', 'Pagos exportados correctamente');
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al exportar pagos');
    }
  };

  const handleTabChange = (newValue: number) => {
    setActiveTab(newValue);
  };

  const tabs = tabsBySource({
    request: paymentGroupedDetails,
    setSuccess,
    setFailure,
    reset,
    onExport: handleExport,
    setActiveTab,
    setUp,
    refreshPaymentGroupDetails,
  });

  return (
    <Dialog maxWidth="lg" fullWidth open={open} onClose={() => setOpen(false)}>
      <div style={{ padding: 20, minHeight: 200 }}>
        <EnhancedTab
          title={`${paymentGroupedDetails.sourceTranslation} ${cellFormat(paymentGroupedDetails.sourceId, 'numeric-id')} `}
          startValue={activeTab}
          tabs={tabs}
          onTabChange={handleTabChange}
          chip={cellFormat(
            paymentGroupedDetails.payoutStatusEsp,
            'status-chip',
          )}
        />
      </div>
      <DialogActions>
        <CustomButton color="secondary" onClick={() => setOpen(false)}>
          {' '}
          Salir{' '}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default PayOutDetails;
